'use client';

import React from 'react';
import SidebarNavigation from './sidebar-navigation';
import styles from './app-layout.module.css';

interface Breadcrumb {
  label: string;
  onClick?: () => void;
}

interface ActionButton {
  label: string;
  onClick: () => void;
  variant: 'primary' | 'secondary';
}

interface AppLayoutProps {
  user: { Email: string; Name: string; Role: string };
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  buttons?: ActionButton[];
  onLogout?: () => void;
  children: React.ReactNode;
}

export default function AppLayout({
  user,
  title,
  subtitle,
  breadcrumbs,
  buttons,
  onLogout,
  children,
}: AppLayoutProps) {
  return (
    <div className={styles.appContainer}>
      {/* Sidebar Navigation */}
      <SidebarNavigation user={user} onLogout={onLogout} />

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Header */}
        <header className={styles.header}>
          {/* Left side - Title, subtitle, and breadcrumbs */}
          <div className={styles.headerLeft}>
            <div className={styles.titleSection}>
              <h1 className={styles.title}>{title}</h1>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>

            {/* Breadcrumbs */}
            {breadcrumbs && breadcrumbs.length > 0 && (
              <nav className={styles.breadcrumbs}>
                {breadcrumbs.map((crumb, index) => (
                  <span key={index} className={styles.breadcrumbItem}>
                    {crumb.onClick ? (
                      <button
                        className={styles.breadcrumbButton}
                        onClick={crumb.onClick}
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span>{crumb.label}</span>
                    )}
                    {index < breadcrumbs.length - 1 && (
                      <span className={styles.breadcrumbSeparator}>/</span>
                    )}
                  </span>
                ))}
              </nav>
            )}
          </div>

          {/* Right side - Action Buttons */}
          <div className={styles.headerRight}>
            {buttons && buttons.length > 0 && (
              <div className={styles.buttonGroup}>
                {buttons.map((button, index) => (
                  <button
                    key={index}
                    className={`${styles.button} ${styles[button.variant]}`}
                    onClick={button.onClick}
                  >
                    {button.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </header>

        {/* Page Content Area */}
        <div className={styles.pageContent}>{children}</div>
      </div>
    </div>
  );
}
